import React, { useState, useEffect, useMemo } from "react";
import { 
  ArrowRight, 
  Calendar as CalendarIcon, 
  Activity,
  Search,
  Database,
  Key,
  Mail
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BudgetCard } from "./BudgetCard";
import { Logo } from "./Logo";
import { UniqueAccordion } from "./UniqueAccordion";
import { GlowingEffect } from "./GlowingEffect";
import { Footer } from "./Footer";
import { Testimonials } from "./Testimonials";

interface LetsWorkTogetherProps {
  onShowPricing?: () => void;
  onShowBooking?: () => void;
  onPillAction?: () => void;
  onBookCall?: (data: { name: string; email: string; date: string }) => void;
}

function NavButton({ label, onClick, variant = "default", className = "" }: { label: string; onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void; variant?: "default" | "primary" | "outline"; className?: string }) {
  return (
    <div className={`relative rounded-full ${className} transform-gpu`}>
      <GlowingEffect
        spread={25}
        glow={true}
        disabled={false}
        proximity={50}
        inactiveZone={0.01}
        borderWidth={1.5}
      />
      <button 
        onClick={onClick}
        className={`relative flex items-center justify-center rounded-full border border-border/50 px-6 py-2.5 backdrop-blur-md transition-all group outline-none focus:ring-1 focus:ring-foreground/20 ${
          variant === "primary" 
            ? "bg-primary text-primary-foreground hover:bg-primary/90" 
            : variant === "outline"
            ? "bg-background/20 text-foreground border-foreground/20 hover:bg-background/40"
            : "bg-background/50 text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        }`}
      >
        <span className="text-[10px] font-bold tracking-[0.3em] uppercase transition-colors">
          {label}
        </span>
      </button>
    </div>
  );
}

export function LetsWorkTogether({ onShowPricing, onShowBooking, onPillAction }: LetsWorkTogetherProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [titleNumber, setTitleNumber] = useState(0);
  const titles = useMemo(
    () => [
      "Recover Profits",
      "Secure Sales",
      "Protect Content",
      "Stay Exclusive",
      "Kill Leaks"
    ],
    []
  );

  useEffect(() => {
    if (isClicked) return;
    const intervalId = setInterval(() => {
      setTitleNumber((prev) => (prev === titles.length - 1 ? 0 : prev + 1));
    }, 2800);
    return () => clearInterval(intervalId);
  }, [isClicked, titles.length]);

  const handleMainAction = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setIsClicked(true);
    setTimeout(() => {
      setShowSuccess(true);
    }, 500);
  };

  return (
    <section className="relative flex min-h-screen flex-col items-center px-6 overflow-x-hidden bg-background transform-gpu">
      {/* Brand Header */}
      <motion.header 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-7xl flex items-center justify-between py-10 z-[60] transform-gpu"
      >
        <Logo />
        <div className="hidden sm:flex items-center gap-4">
          <NavButton label="Book a Call" variant="outline" onClick={onShowBooking} />
          <NavButton label="Pricing" variant="primary" onClick={onShowPricing} />
        </div>
      </motion.header>

      <div className="flex-1 flex flex-col items-center justify-center gap-16 max-w-6xl w-full pb-32 transform-gpu relative">
        <AnimatePresence mode="wait">
          {showSuccess ? (
            <motion.div
              key="success-state"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-20 flex flex-col items-center justify-center px-6 transform-gpu"
            >
              <div className="flex flex-col items-center gap-2 mb-12 text-center">
                <motion.span 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-xs font-medium tracking-[0.4em] uppercase text-muted-foreground"
                >
                  Authorization Verified
                </motion.span>
                <motion.h3 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-4xl font-light tracking-tight text-foreground sm:text-5xl md:text-6xl"
                >
                  Ready to deploy?
                </motion.h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-2xl">
                <div className="w-full">
                  <ChoiceCard 
                    title="Schedule Strategy"
                    description="Talk to our team about securing your products and high-volume protection."
                    icon={<CalendarIcon className="size-6" />}
                    onClick={onShowBooking}
                    delay={0.3}
                  />
                </div>
                <div className="w-full">
                  <ChoiceCard 
                    title="Direct Inquiry"
                    description="Send our team an email to discuss custom enterprise requirements."
                    icon={<Mail className="size-6" />}
                    onClick={() => window.location.href = 'mailto:aureos.app@gmail.com'}
                    delay={0.4}
                  />
                </div>
              </div>

              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="mt-16 flex items-center gap-8"
              >
                <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground/30 font-medium cursor-pointer hover:text-muted-foreground transition-colors" onClick={() => {setIsClicked(false); setShowSuccess(false);}}>
                  Back to Overview
                </span>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="landing-state"
              initial={{ opacity: 1 }}
              animate={isClicked ? { opacity: 0, scale: 0.95, filter: "blur(10px)" } : { opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="w-full flex flex-col items-center gap-16 transform-gpu"
            >
              <div 
                onClick={onPillAction}
                className="relative rounded-full cursor-pointer transform-gpu group"
              >
                <GlowingEffect
                  spread={25}
                  glow={true}
                  disabled={false}
                  proximity={50}
                  inactiveZone={0.01}
                  borderWidth={1.5}
                />
                <div className="relative flex items-center justify-center rounded-full border border-border/50 px-6 py-2.5 backdrop-blur-md bg-background/50 text-muted-foreground group-hover:text-foreground transition-colors">
                  <span className="text-[10px] font-bold tracking-[0.3em] uppercase">
                    Security for Whop Creators
                  </span>
                </div>
              </div>

              <div
                className="group relative cursor-pointer transform-gpu w-full px-4 mb-16"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleMainAction}
              >
                <div className="flex flex-col items-center gap-2">
                  <h2 className="relative text-center text-4xl font-light tracking-tight text-foreground sm:text-6xl md:text-7xl lg:text-8xl leading-[1.1]">
                    <span className="block overflow-hidden pb-1">
                      <motion.span
                        className="block"
                        animate={{ y: isHovered ? "-2%" : "0%" }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                      >
                        Stop Whop Resellers
                      </motion.span>
                    </span>
                    <div className="relative flex w-full justify-center text-center min-h-[1.2em] py-1 overflow-visible">
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={titleNumber}
                          initial={{ y: 20, opacity: 0, filter: "blur(5px)" }}
                          animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                          exit={{ y: -20, opacity: 0, filter: "blur(5px)" }}
                          transition={{ type: "spring", stiffness: 100, damping: 20, mass: 0.8 }}
                          className="text-brand-gold block"
                        >
                          {titles[titleNumber]}
                        </motion.span>
                      </AnimatePresence>
                    </div>
                  </h2>
                  <p className="max-w-2xl text-lg md:text-xl font-light text-muted-foreground mt-4 text-center leading-relaxed px-4">
                    Most digital products leak within 48 hours. We find the leaks and lock your files so only paying members have access.
                  </p>
                </div>
              </div>

              {/* Main Info Grid */}
              <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-20 items-start px-4 transform-gpu mt-20">
                <div className="flex flex-col gap-10">
                  <div className="space-y-4">
                    <h4 className="text-[10px] font-bold tracking-[0.5em] uppercase text-muted-foreground/30">What We Do</h4>
                    <p className="text-2xl font-light tracking-tight text-foreground max-w-sm">
                      Smart security built for the world's top digital creators.
                    </p>
                  </div>
                  <UniqueAccordion />
                </div>
                <div className="flex flex-col items-center gap-12 lg:sticky lg:top-32 transform-gpu">
                  <div className="space-y-4 text-center lg:text-left w-full">
                    <h4 className="text-[10px] font-bold tracking-[0.5em] uppercase text-muted-foreground/30">Safety Report</h4>
                    <p className="text-2xl font-light tracking-tight text-foreground">
                      Total Revenue Protected
                    </p>
                  </div>
                  <BudgetCard />
                </div>
              </div>

              {/* Infrastructure & Technical Depth */}
              <div className="w-full py-32 space-y-20 transform-gpu">
                <div className="text-center space-y-4 max-w-2xl mx-auto">
                  <h4 className="text-[10px] font-bold tracking-[0.5em] uppercase text-primary">Our Technology</h4>
                  <h2 className="text-4xl md:text-5xl font-light tracking-tight">The foundation of <span className="italic">digital scarcity</span>.</h2>
                  <h3 className="text-muted-foreground font-light leading-relaxed">Our custom protection layer ensures your files are served only to those who paid for them, making reselling impossible.</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                   <InfoMetricCard 
                    icon={<Database />} 
                    title="Encrypted Storage" 
                    sub="Secure Vault" 
                    desc="High-level encryption ensures your files stay safe and private on our secure servers." 
                   />
                   <InfoMetricCard 
                    icon={<Activity />} 
                    title="Access Guard" 
                    sub="Real-time Tracking" 
                    desc="Our system monitors every download to ensure only verified members have access." 
                   />
                   <InfoMetricCard 
                    icon={<Search />} 
                    title="Active Patrol" 
                    sub="24/7 Web Scan" 
                    desc="We search the web every hour to find and remove any unauthorized copies of your work." 
                   />
                   <InfoMetricCard 
                    icon={<Key />} 
                    title="One-Time Tokens" 
                    sub="Unique Access" 
                    desc="Every single access request is verified with a unique token that cannot be shared or reused." 
                   />
                </div>
              </div>

              <div className="w-full py-12 transform-gpu">
                <Testimonials />
              </div>

              {/* Tagline */}
              <div className="flex flex-col items-center gap-6 text-center transform-gpu">
                <div className="flex items-center gap-8">
                   <span className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground/40">Encrypted</span>
                   <span className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground/40">Scalable</span>
                   <span className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground/40">Silent</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {!isClicked && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full transform-gpu">
            <Footer />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function InfoMetricCard({ icon, title, sub, desc }: { icon: React.ReactNode; title: string; sub: string; desc: string }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative p-8 rounded-[32px] border border-border/40 bg-card/20 backdrop-blur-3xl overflow-hidden group"
    >
      <GlowingEffect spread={80} glow={isHovered} disabled={false} proximity={120} inactiveZone={0.01} borderWidth={1.5} />
      <div className="relative z-10 space-y-6">
        <div className="size-12 rounded-2xl bg-muted/20 flex items-center justify-center text-muted-foreground group-hover:text-primary group-hover:bg-primary/5 transition-all duration-500">
          {icon}
        </div>
        <div className="space-y-2">
          <p className="text-[9px] font-bold tracking-[0.4em] uppercase text-primary">{sub}</p>
          <h3 className="text-2xl font-light tracking-tighter text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground/60 leading-relaxed font-light">{desc}</p>
        </div>
      </div>
    </div>
  );
}

function ChoiceCard({ title, description, icon, onClick, delay }: { title: string; description: string; icon: React.ReactNode; onClick?: () => void; delay: number }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={onClick} className="group relative flex w-full flex-col items-start gap-4 rounded-3xl border border-border/50 bg-card/40 p-8 text-left transition-all hover:bg-card/60 hover:border-primary/20 hover:scale-[1.02] overflow-hidden transform-gpu h-full">
      <GlowingEffect spread={60} glow={isHovered} disabled={false} proximity={120} inactiveZone={0.01} borderWidth={2} />
      <div className="relative z-10 flex size-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/10">{icon}</div>
      <div className="relative z-10 space-y-1">
        <h4 className="text-xl font-medium text-foreground">{title}</h4>
        <p className="text-sm text-muted-foreground/80 leading-relaxed">{description}</p>
      </div>
      <div className="relative z-10 mt-6 flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-primary/70 group-hover:text-primary transition-colors">
        Action Required <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
      </div>
    </motion.button>
  );
}